import React, { useEffect, useRef, useState } from "react";
import { consumeUpdateLanded } from "../lib/cacheVersion";

// =============================================================================
// UPDATE LANDED TOAST
// =============================================================================
// The visual cue after a SILENT deploy reload. useVersionPoll drops a
// sessionStorage breadcrumb (markUpdateLanded) right before reloadAndWipe on the
// clean-tab path; this toast reads + clears it on the fresh boot and shows a
// brief "New Altara update just landed" pill. Without it a clean reload would be
// invisible — the page just blinks. This replaces the information the old
// blocking confirm() used to convey ("you're being updated").
//
// Mounted once in AppLayout next to UpdateBanner. Self-dismisses (~3.8s) — no
// user action. The two never collide: the banner is the dirty/idle path (pre
// reload), the toast is the clean path (post reload).
//
// GLASS NOTE: the anim-toast-* transform lives on the OUTER wrapper; the inner
// pill is the liquid-glass surface. Animating transform on glass directly would
// trigger a backdrop-filter repaint that blurs the text mid-slide (css-glass.md).
// =============================================================================

const VISIBLE_MS = 3800;
const EXIT_MS = 280; // must cover anim-toast-out (260ms) + a hair

export default function UpdateLandedToast(): React.ReactElement | null {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  // Read the one-shot breadcrumb EXACTLY once. The ref survives React 19
  // StrictMode's dev double-invoke of effects (consume() would otherwise return
  // true on the 1st run and false on the 2nd, losing the dismiss timers). With
  // the ref, the decision is cached and the timers re-arm on whichever run wins.
  const shouldShowRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (shouldShowRef.current === null) shouldShowRef.current = consumeUpdateLanded();
    if (!shouldShowRef.current) return;
    setVisible(true); // eslint-disable-line react-hooks/set-state-in-effect -- one-time mount read of an external sessionStorage signal; mirrors GoalHeader's prop-sync pattern
    const startExit = window.setTimeout(() => setLeaving(true), VISIBLE_MS);
    const unmount = window.setTimeout(() => setVisible(false), VISIBLE_MS + EXIT_MS);
    return () => {
      window.clearTimeout(startExit);
      window.clearTimeout(unmount);
    };
  }, []);

  if (!visible) return null;

  return (
    // Non-glass wrapper carries the transform animation (glass-safe).
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-40 w-max max-w-[calc(100vw-2rem)] pointer-events-none ${
        leaving ? "anim-toast-out" : "anim-toast-in"
      }`}
      role="status"
      aria-live="polite"
    >
      {/* liquid-glass-light pill — overflow:hidden + 16px radius from the class */}
      <div className="liquid-glass-light bg-ds-green/10 flex items-center gap-2.5 px-5 py-2.5">
        {/* Success dot */}
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-ds-green/20 shrink-0">
          <svg
            viewBox="0 0 24 24"
            className="w-3 h-3 text-ds-green"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </span>
        <span className="token-14 font-medium text-glass whitespace-nowrap">
          New Altara update just landed
        </span>
      </div>
    </div>
  );
}
